import { AgGridButtons } from "@/controls/buttons/AgGridButtons";
import { AgGridHeaderButtons } from "@/controls/buttons/AgGridHeaderButtons";
import { ProcessData } from "@/pages/caster/models/ProcessData";
import { StrandData } from "@/pages/caster/models/StrandData";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { useProductionTableStyles } from "../styles/useProductionTableStyles";

interface ProductionTableHookProps {
	strands: StrandData[];
	processData: ProcessData | null;
	editCallback: (strandId: number | null) => void;
	readonly: boolean;
}

export const useProductionTableData = ({
	strands,
	processData,
	editCallback,
	readonly,
}: ProductionTableHookProps) => {
	const classes = useProductionTableStyles();

	const formatLength = (lengthInInches: number | null | undefined): string => {
		if (lengthInInches === null || lengthInInches === undefined) return "";
		const feet = Math.floor(lengthInInches / 12);
		const inches = lengthInInches % 12;
		return `${feet} ft ${inches} in`;
	};

	const columns: ColGroupDef[] = [
		{
			headerName: "Production",
			children: [
				{
					field: "Strand",
					headerName: "",
					flex: 1,
					minWidth: 78,
					valueGetter: (params) => `Strand ${params.data.Strand}`,
					cellClass: "first-column-cell",
				},
				{
					field: "PieceLength",
					headerName: "Length",
					flex: 1,
					minWidth: 83,
					valueFormatter: (params) => formatLength(params.value),
				},
				{
					field: "Pieces",
					headerName: "Pieces",
					flex: 1,
					minWidth: 64,
				},
				{
					field: "SectionName",
					headerName: "Section",
					flex: 1,
					minWidth: 70,
				},
				{
					field: "actions",
					headerComponent: readonly
						? undefined
						: (params: ICellRendererParams) => {
								return (
									<AgGridHeaderButtons
										editAllLabel="Edit all strands"
										editAllItems={() => editCallback(null)}
									/>
								);
							},
					hide: readonly,
					flex: 1,
					minWidth: 65,
					maxWidth: 65,
					cellRenderer: readonly
						? undefined
						: (params: ICellRendererParams) => (
								<AgGridButtons
									editItem={() =>
										editCallback((params.data as StrandData).Strand)
									}
								/>
							),
				},
			],
		},
	];

	const defaultColDef: ColDef = {
		filter: false,
		floatingFilter: false,
		sortable: false,
		resizable: false,
		editable: false,
		suppressHeaderMenuButton: true,
	};

	const rowData = strands;

	const statusBar = {
		statusPanels: [
			{
				statusPanel: "agAggregationComponent",
				statusPanelParams: {
					aggFuncs: ["sum"],
				},
			},
			{
				statusPanel: () => (
					<div
						style={{
							textAlign: "center",
							width: "100%",
						}}
					>
						Tons Cast{" "}
						{processData?.CastTons !== null &&
						processData?.CastTons !== undefined
							? processData.CastTons.toFixed(1)
							: ""}
					</div>
				),
				align: "center",
			},
		],
	};

	return { columns, defaultColDef, rowData, statusBar };
};
